import { isBR, MARKET, pickMarketRows } from "@/lib/market";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Menu, User, Settings, ShoppingCart, LogOut, Briefcase } from "lucide-react";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCart";
import { usePaymentSettings } from "@/hooks/usePaymentSettings";
import { supabase } from "@/integrations/supabase/client";
import { MiniCart } from "./MiniCart";
import { PWAInstallButton } from "@/components/PWAInstallButton";
import { LanguageSelector } from "@/components/LanguageSelector";
import { GlobalSearch, GlobalSearchMobile } from "./GlobalSearch";
import { MegaMenuPopover } from "./MegaMenuPopover";
import { MobileMegaMenu } from "./MobileMegaMenu";
import { MoreMenuPopover } from "./MoreMenuPopover";
import { UserMenuPopover } from "./UserMenuPopover";
import { resolveEffectiveModule } from "@/utils/roleNavigation";
import { useModulePermissions } from "@/hooks/useModulePermissions";
import { useLocalizedField } from "@/lib/i18n-db";

const navLinkClass = "inline-flex h-10 items-center justify-center rounded-md px-2 xl:px-3 py-2 text-sm font-medium text-foreground transition-colors hover:text-primary hover:bg-accent focus:outline-none";

const FALLBACK_EQUIPMENT_CATEGORIES = [
  { id: "fallback-equipment-dtf", name: "Impressoras DTF", slug: "impressora-dtf", description: "Equipamentos DTF profissionais" },
  { id: "fallback-equipment-uv", name: "Impressoras UV", slug: "impressora-uv", description: "Impressão UV para rígidos" },
  { id: "fallback-equipment-sublimation", name: "Sublimação", slug: "sublimacao", description: "Soluções de sublimação" },
  { id: "fallback-equipment-presses", name: "Prensas", slug: "prensas", description: "Prensas térmicas e acabamento" },
];

const FALLBACK_CONSUMABLE_CATEGORIES = [
  { id: "fallback-consumables-inks", name: "Tintas DTF", slug: "tintas-dtf", description: "Tintas DTF certificadas" },
  { id: "fallback-consumables-film", name: "Filmes DTF", slug: "peliculas-de-impressao", description: "Filmes PET e materiais" },
  { id: "fallback-consumables-powder", name: "Pó DTF", slug: "po-transferencia", description: "Pó adesivo hot melt" },
  { id: "fallback-consumables-parts", name: "Peças e Acessórios", slug: "pecas-acessorios", description: "Peças, limpeza e acessórios" },
];

const FALLBACK_HEADER_NAV_ITEMS = [
  { id: "fallback-solutions", url: "/solucoes", label: "Soluções", opens_in_new_tab: false },
  { id: "fallback-uv", url: "/impressoras-uv", label: "Impressoras UV", opens_in_new_tab: false },
  { id: "fallback-support", url: "/assistencia", label: "Assistência", opens_in_new_tab: false },
  { id: "fallback-contact", url: "/contacto", label: "Contacto", opens_in_new_tab: false },
  { id: "fallback-financing", url: "/financiamento", label: "Financiamento", opens_in_new_tab: false },
  { id: "fallback-about", url: "/sobre", label: "Sobre", opens_in_new_tab: false },
];

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<"equipment" | "consumables">("equipment");
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const { t, i18n } = useTranslation();
  const {
    user,
    signOut,
    isAdmin,
    userRole,
    profile
  } = useAuth();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;
      if (isMobileMenuOpen) {
        setIsHidden(false);
      } else if (y < 80) {
        setIsHidden(false);
      } else if (delta > 6) {
        setIsHidden(true);
      } else if (delta < -6) {
        setIsHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobileMenuOpen]);

  // Expose auto-hide state so sticky elements (category pills) can rise to top:0
  useEffect(() => {
    document.documentElement.dataset.headerHidden = isHidden ? "true" : "false";
  }, [isHidden]);

  const { permissions: modulePermissions } = useModulePermissions();
  const {
    itemCount
  } = useCart();
  const {
    cartEnabled
  } = usePaymentSettings();
  const navigate = useNavigate();
  const lf = useLocalizedField();

  // Fetch equipment categories from database
  const {
    data: equipmentCategories
  } = useQuery({
    queryKey: ["menu-equipment-categories"],
    queryFn: async () => {
      const { data, error } = await supabase.from("equipment_categories").select("*").order("display_order");
      if (error) throw error;
      return data;
    }
  });

  // Fetch consumables categories from database  
  const {
    data: consumablesCategories
  } = useQuery({
    queryKey: ["menu-consumables-categories"],
    queryFn: async () => {
      const { data, error } = await supabase.from("consumables_categories").select("*").order("display_order");
      if (error) throw error;
      return data;
    }
  });

  // Fetch header navigation items from database
  const {
    data: headerNavItems
  } = useQuery({
    queryKey: ["header-navigation", MARKET],
    queryFn: async () => {
      const { data, error } = await supabase.from("navigation_settings").select("*").eq("location", "header").eq("is_visible", true).in("market", [MARKET, "pt"]).order("display_order");
      if (error) throw error;
      return pickMarketRows(data as any, MARKET);
    }
  });

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // Filter categories that should show in menu (show_in_menu !== false)
  const visibleEquipmentCategories = equipmentCategories?.filter((cat: any) => cat.show_in_menu !== false) || [];
  const visibleConsumablesCategories = consumablesCategories?.filter((cat: any) => cat.show_in_menu !== false) || [];
  const menuEquipmentCategories = visibleEquipmentCategories.length > 0 ? visibleEquipmentCategories : FALLBACK_EQUIPMENT_CATEGORIES;
  const menuConsumablesCategories = visibleConsumablesCategories.length > 0 ? visibleConsumablesCategories : FALLBACK_CONSUMABLE_CATEGORIES;

  const effectiveModule = resolveEffectiveModule(
    userRole,
    modulePermissions,
    (profile as any)?.preferred_module
  );
  const isInternal = !!effectiveModule;
  const moduleInfo = effectiveModule;

  // Map DB nav item URLs to translation keys
  const navLabelMap: Record<string, string> = {
    '/solucoes': 'nav.solutions',
    '/assistencia': 'nav.support',
    '/contacto': 'nav.contact',
    '/financiamento': 'homeSolutions.financing.title',
    '/sobre': 'nav.about',
  };

  const getNavLabel = (item: any) => {
    const key = navLabelMap[item.url];
    if (key) return t(key);
    const lang = (i18n.language || "pt").split("-")[0];
    if (lang !== "pt") {
      const localized = item[`label_${lang}`];
      if (localized && String(localized).trim() !== "") return localized;
    }
    return item.label;
  };

  const visibleHeaderNavItems = headerNavItems && headerNavItems.length > 0 ? headerNavItems : FALLBACK_HEADER_NAV_ITEMS;

  // Mobile user icon destination
  const mobileUserLink = !user ? "/auth" : isInternal && moduleInfo ? moduleInfo.path : "/conta";

  return (
    <header
      data-app-header="true"
      className={`fixed inset-x-0 top-0 z-[2147483600] h-16 w-full border-b border-border/50 bg-background/95 shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-background/90 transition-transform duration-300 will-change-transform ${isHidden ? "-translate-y-full" : "translate-y-0"}`}
    >
      <div className="container flex h-16 items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" style={{
          minWidth: '119px',
          minHeight: '44px'
        }}>
          <img 
            alt="HEXATECH - Impressão Digital Profissional DTF" 
            className="h-11 md:h-11 object-contain" 
            src="/lovable-uploads/aeb53278-3218-454b-9634-6870a190faab.png" 
            width={119} 
            height={44} 
            style={{
              aspectRatio: '119/44',
              width: 'auto'
            }} 
            decoding="async" 
          />
        </Link>

        {/* Desktop Navigation - Click-based menus */}
        <nav className="hidden lg:flex items-center gap-0 min-h-[40px]">
          {/* Equipment Dropdown */}
          {<MegaMenuPopover 
            categories={menuEquipmentCategories} 
            type="equipment" 
            label={t("nav.equipment")}
          />}

          {/* Consumables Dropdown */}
          <MegaMenuPopover 
            categories={menuConsumablesCategories} 
            type="consumables" 
            label={t("nav.consumables")}
          />

          {/* Dynamic navigation items from database (excluding shop and blog) */}
          {visibleHeaderNavItems.filter((item: any) => 
            !item.url.includes('/loja') && !(isBR && (item.url.includes('/solucoes') || item.url.includes('/assistencia'))) && !item.url.includes('/blog') && !item.url.includes('/impressoras-uv') && !item.url.includes('/financiamento')
          ).map((item: any) => (
            <div key={item.id}>
              {item.opens_in_new_tab ? (
                <a 
                  href={item.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={navLinkClass}
                >
                  {getNavLabel(item)}
                </a>
              ) : (
                <Link to={item.url} className={navLinkClass}>
                  {getNavLabel(item)}
                </Link>
              )}
            </div>
          ))}

          {/* More Menu Dropdown */}
          <MoreMenuPopover />
        </nav>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-1">
          {/* Global Search */}
          <GlobalSearch />
          
          {!isBR && <LanguageSelector variant="minimal" />}

          {/* Notification Bell - Only for authenticated users */}
          {user && <NotificationBell />}
          
          {/* Cart Icon - visible for all users (internal users are also clients) */}
          {cartEnabled && <MiniCart itemCount={itemCount} />}
          
          {user ? (
            <UserMenuPopover 
              isAdmin={isAdmin} 
              cartEnabled={cartEnabled} 
              onSignOut={handleSignOut}
              effectiveModule={effectiveModule}
            />
          ) : (
            <Button 
              variant="ghost" 
              size="icon" 
              asChild 
              className="text-foreground/80 hover:text-primary hover:bg-accent"
            >
              <Link to="/auth" aria-label={t("nav.login")}>
                <User className="h-5 w-5" />
              </Link>
            </Button>
          )}
        </div>

        {/* Mobile Actions - Icons always visible */}
        <div className="flex lg:hidden items-center gap-0.5">
          {/* Search Icon - Mobile */}
          <GlobalSearchMobile />

          {/* Notification Bell - Mobile */}
          {user && <NotificationBell />}
          
          {/* Cart Icon - Mobile (visible for all users) */}
          {cartEnabled && (
            <Button 
              variant="ghost" 
              size="icon" 
              asChild 
              className="text-foreground/80 hover:text-primary hover:bg-accent relative"
            >
              <Link to="/carrinho" aria-label="Carrinho">
                <ShoppingCart className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-[10px] font-semibold text-primary-foreground flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
            </Button>
          )}
          
          {/* User Icon - Mobile */}
          <Button 
            variant="ghost" 
            size="icon" 
            asChild 
            className="text-foreground/80 hover:text-primary hover:bg-accent"
          >
            <Link 
              to={mobileUserLink} 
              aria-label={user ? t("nav.myAccount") : t("nav.login")}
            >
              <User className="h-5 w-5" />
            </Link>
          </Button>
          
          {/* Menu Hamburger */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground/70 h-10 w-10">
                <Menu className="h-6 w-6" />
                <span className="sr-only">{t("nav.openMenu")}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-[380px] p-4 bg-background overflow-y-auto z-[2147483602]">
              <nav className="flex flex-col gap-3 mt-6 pb-8">
                <div className={"grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted"}>
                  {(["equipment", "consumables"] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setMobileTab(tab)}
                      className={`py-2 rounded-md text-sm font-semibold transition-colors ${
                        mobileTab === tab ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
                      }`}
                    >
                      {tab === "equipment" ? "Equipamentos" : "Consumíveis"}
                    </button>
                  ))}
                </div>
                <MobileMegaMenu
                  key={mobileTab}
                  categories={mobileTab === "equipment" ? menuEquipmentCategories : menuConsumablesCategories}
                  type={mobileTab}
                  onLinkClick={() => setIsMobileMenuOpen(false)}
                />
                <div className="border-t border-border" />

                {/* Dynamic navigation items for mobile */}
                {visibleHeaderNavItems.filter((item: any) => !(isBR && /\/(solucoes|assistencia|financiamento)/.test(item.url))).map((item: any) => 
                  item.opens_in_new_tab ? (
                    <a 
                      key={item.id} 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-lg font-medium text-foreground hover:text-primary transition-colors" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {getNavLabel(item)}
                    </a>
                  ) : (
                    <Link 
                      key={item.id} 
                      to={item.url} 
                      className="text-lg font-medium text-foreground hover:text-primary transition-colors" 
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {getNavLabel(item)}
                    </Link>
                  )
                )}

                <div className="border-t border-border pt-4 mt-4 flex flex-col gap-3">
                  {/* Language Selector in Mobile */}
                  <div className="mb-4 pb-4 border-b border-border">
                    {!isBR && <LanguageSelector variant="footer" showLabel={false} />}
                  </div>
                  
                  {user ? (
                    <>
                      {isAdmin && (
                        <Link 
                          to="/admin" 
                          className="text-lg font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-2" 
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Settings className="h-5 w-5" />
                          {t("nav.adminPanel")}
                        </Link>
                      )}
                      {isInternal && moduleInfo ? (
                        <>
                          <Link 
                            to={moduleInfo.path} 
                            className="text-lg font-medium text-foreground hover:text-primary transition-colors flex items-center gap-2" 
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <Briefcase className="h-5 w-5" />
                            {moduleInfo.label}
                          </Link>
                          <Link 
                            to="/conta?tab=profile" 
                            className="text-lg font-medium text-foreground hover:text-primary transition-colors" 
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Perfil
                          </Link>
                        </>
                      ) : (
                        <>
                          <Link 
                            to="/conta" 
                            className="text-lg font-medium text-foreground hover:text-primary transition-colors" 
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {t("nav.myAccount")}
                          </Link>
                          {cartEnabled && (
                            <Link 
                              to="/carrinho" 
                              className="text-lg font-medium text-foreground hover:text-primary transition-colors flex items-center gap-2" 
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {t("nav.cart")}
                              {itemCount > 0 && (
                                <span className="h-5 w-5 rounded-full bg-primary text-[10px] font-semibold text-primary-foreground flex items-center justify-center">
                                  {itemCount}
                                </span>
                              )}
                            </Link>
                          )}
                        </>
                      )}
                      <button 
                        onClick={() => {
                          handleSignOut();
                          setIsMobileMenuOpen(false);
                        }} 
                        className="text-left text-lg font-medium text-destructive hover:opacity-80 transition-opacity"
                      >
                        {t("nav.logout")}
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <Button asChild variant="outline" className="w-full justify-center">
                        <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)}>
                          {t("nav.login")}
                        </Link>
                      </Button>
                      <Button asChild className="w-full justify-center bg-primary hover:bg-primary/90">
                        <Link to="/auth?mode=register" onClick={() => setIsMobileMenuOpen(false)}>
                          {t("nav.register")}
                        </Link>
                      </Button>
                      <PWAInstallButton variant="secondary" className="w-full justify-center" />
                    </div>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
